import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import EmployeeSidebar from '../../components/employee/EmployeeSidebar';
import LegalFooter from '../../components/shared/LegalFooter';
import api from '../../utils/api';
import PropertyPricingPanel from '../../components/employee/PropertyPricingPanel';
import PropertyMap from '../../components/shared/PropertyMap';
import { useAuth } from '../../hooks/useAuth';

const ISP_UPGRADE_URLS = {
  'Spectrum': 'https://www.spectrum.com/my-account',
  'Optimum': 'https://www.optimum.net/account',
  'RCN': 'https://www.rcn.com/account',
  'Verizon Fios': 'https://www.verizon.com/home/myverizon',
  'Comcast': 'https://www.xfinity.com/myaccount',
};

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
const fmtMoney = (n) => n ? '$' + parseFloat(n).toFixed(2) : '—';
const lbl = { fontSize: 12, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 };
const inp = { width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, color: '#1e293b', outline: 'none', background: 'white' };

const STATUS_CONFIG = {
  active:   { label: 'Active',   bg: '#dcfce7', color: '#16a34a' },
  draft:    { label: 'Draft',    bg: '#f1f5f9', color: '#64748b' },
  inactive: { label: 'Inactive', bg: '#fee2e2', color: '#dc2626' },
};

export default function PropertyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { role } = useAuth();
  const isSuperAdmin = role === 'super_admin';
  const [property, setProperty] = useState(null);
  const [units, setUnits] = useState([]);
  const [ispContacts, setIspContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState({});
  const [launchDate, setLaunchDate] = useState('');
  const [rampUpDate, setRampUpDate] = useState('');
  const [goLiveDate, setGoLiveDate] = useState('');
  const [showLaunchDate, setShowLaunchDate] = useState(false);
  const [showRampDates, setShowRampDates] = useState(false);
  const [showNewContact, setShowNewContact] = useState(false);
  const [newContact, setNewContact] = useState({ first_name: '', last_name: '', title: '', email: '', phone: '' });
  const [savingContact, setSavingContact] = useState(false);
  const [preApprovals, setPreApprovals] = useState([]);
  const [showAddPA, setShowAddPA] = useState(false);
  const [paForm, setPaForm] = useState({ unit_number: '', first_name: '', last_name: '', email: '', phone: '', move_in_date: '', notes: '' });
  const [savingPA, setSavingPA] = useState(false);

  useEffect(() => {
    api.get('/employee/properties/' + id)
      .then(res => {
        setProperty(res.data.property);
        setUnits(res.data.units);
        setForm(res.data.property);
        setLaunchDate(res.data.property.launch_date ? res.data.property.launch_date.split('T')[0] : '');
        setRampUpDate(res.data.property.ramp_up_date ? res.data.property.ramp_up_date.split('T')[0] : '');
        setGoLiveDate(res.data.property.go_live_date ? res.data.property.go_live_date.split('T')[0] : '');
        if (res.data.property.isp) {
          return api.get('/employee/isp-contacts?isp=' + res.data.property.isp);
        }
      })
      .then(res => { if (res) setIspContacts(res.data.contacts); })
      .catch(() => setError('Could not load property'))
      .finally(() => setLoading(false));
    api.get('/pre-approvals?property_id=' + id)
      .then(res => setPreApprovals(res.data.pre_approvals || []))
      .catch(() => {});
  }, [id]);

  const handleChange = async (e) => {
    const { name, value, type, checked } = e.target;
    const newVal = type === 'checkbox' ? checked : value;
    setForm(prev => ({ ...prev, [name]: newVal }));
    if (name === 'isp' && ISP_UPGRADE_URLS[value]) {
      setForm(prev => ({ ...prev, isp_upgrade_url: ISP_UPGRADE_URLS[value], isp_contact_name: '', isp_contact_email: '', isp_contact_phone: '' }));
      try {
        const res = await api.get('/employee/isp-contacts?isp=' + value);
        setIspContacts(res.data.contacts);
      } catch (e) {}
    }
  };

  const handleContactSelect = (contactId) => {
    if (!contactId) {
      setForm(prev => ({ ...prev, isp_contact_name: '', isp_contact_email: '', isp_contact_phone: '' }));
      return;
    }
    const contact = ispContacts.find(c => c.id === contactId);
    if (contact) {
      setForm(prev => ({
        ...prev,
        isp_contact_name: contact.first_name + ' ' + contact.last_name + (contact.title ? ' — ' + contact.title : ''),
        isp_contact_email: contact.email || '',
        isp_contact_phone: contact.phone || '',
      }));
    }
  };

  const saveNewContact = async () => {
    if (!newContact.first_name || !newContact.last_name) return;
    setSavingContact(true);
    try {
      await api.post('/employee/isp-contacts', { ...newContact, isp: form.isp || property?.isp });
      const res = await api.get('/employee/isp-contacts?isp=' + (form.isp || property?.isp));
      setIspContacts(res.data.contacts);
      setShowNewContact(false);
      setNewContact({ first_name: '', last_name: '', title: '', email: '', phone: '' });
    } catch (err) {
      alert(err.response?.data?.error || 'Could not save contact');
    } finally { setSavingContact(false); }
  };

  const handleSave = async () => {
    setSaving(true); setError(''); setSuccess('');
    try {
      const res = await api.patch('/employee/properties/' + id + '/contract', form);
      setProperty(res.data.property);
      setForm(res.data.property);
      setEditing(false);
      setSuccess('Contract updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save contract');
    } finally { setSaving(false); }
  };

  const handleStatusChange = async (newStatus) => {
    setStatusSaving(true); setError(''); setSuccess('');
    try {
      const res = await api.patch('/employee/properties/' + id + '/status', {
        status: newStatus,
        launch_date: launchDate || null,
        ramp_up_date: rampUpDate || null,
        go_live_date: goLiveDate || null,
      });
      setProperty(prev => ({ ...prev, status: res.data.property.status, launch_date: res.data.property.launch_date, ramp_up_date: res.data.property.ramp_up_date, go_live_date: res.data.property.go_live_date }));
      setSuccess('Property status updated to ' + newStatus + '!');
      setShowLaunchDate(false);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not update status');
    } finally { setStatusSaving(false); }
  };

  if (loading) return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f8f9fa' }}>
      <EmployeeSidebar />
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666' }}>Loading property...</div>
    </div>
  );

  const activeUnits = units.filter(u => u.tenant_id).length;
  const contractExpiry = property?.contract_end_date ? new Date(property.contract_end_date) : null;
  const daysToExpiry = contractExpiry ? Math.ceil((contractExpiry - new Date()) / (1000 * 60 * 60 * 24)) : null;
  const expiryWarning = daysToExpiry !== null && daysToExpiry < 90;
  const statusCfg = STATUS_CONFIG[property?.status] || STATUS_CONFIG['draft'];

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f8f9fa' }}>
      <EmployeeSidebar />
      <div style={{ flex: 1, padding: '2rem', maxWidth: 1100 }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
          <div>
            <button onClick={() => navigate('/employee/landlords')} style={{ background: 'none', border: 'none', color: '#666', cursor: 'pointer', fontSize: 13, marginBottom: 8, padding: 0 }}>← Back to Landlords</button>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
              <h1 style={{ fontSize: '1.8rem', fontWeight: 700, color: '#1e293b', margin: 0 }}>{property?.name}</h1>
              <span style={{ background: statusCfg.bg, color: statusCfg.color, borderRadius: 100, padding: '4px 14px', fontSize: 12, fontWeight: 700 }}>
                {statusCfg.label}
              </span>
              {property?.launch_date && (
                <span style={{ fontSize: 12, color: '#64748b' }}>
                  🗓 Launch: {fmtDate(property.launch_date)}
                </span>
              )}
              {property?.ramp_up_date && (
                <span style={{ fontSize: 12, color: new Date(property.ramp_up_date) <= new Date() ? '#16a34a' : '#f59e0b' }}>
                  📧 Ramp: {fmtDate(property.ramp_up_date)}
                </span>
              )}
              {property?.go_live_date && (
                <span style={{ fontSize: 12, color: new Date(property.go_live_date) <= new Date() ? '#16a34a' : '#f59e0b' }}>
                  💳 Billing: {fmtDate(property.go_live_date)}
                </span>
              )}
            </div>
            <div style={{ fontSize: 14, color: '#64748b' }}>{property?.address}, {property?.city}, {property?.state} {property?.zip}</div>
            <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 4 }}>Landlord: {property?.landlord_name} · {property?.landlord_email}</div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
            {/* Contract edit buttons */}
            <div style={{ display: 'flex', gap: 10 }}>
              {editing ? (
                <>
                  <button onClick={() => { setEditing(false); setForm(property); }} style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 18px', cursor: 'pointer', fontSize: 14 }}>Cancel</button>
                  <button onClick={handleSave} disabled={saving} style={{ background: '#2563eb', color: 'white', border: 'none', borderRadius: 8, padding: '8px 18px', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
                    {saving ? 'Saving...' : 'Save Contract'}
                  </button>
                </>
              ) : (
                <button onClick={() => setEditing(true)} style={{ background: '#2563eb', color: 'white', border: 'none', borderRadius: 8, padding: '8px 18px', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>Edit Contract</button>
              )}
            </div>

            {/* Status action buttons */}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {property?.status !== 'active' && (
                <button onClick={() => handleStatusChange('active')} disabled={statusSaving}
                  style={{ background: '#16a34a', color: 'white', border: 'none', borderRadius: 8, padding: '6px 14px', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                  ✅ Activate
                </button>
              )}
              {property?.status !== 'draft' && (
                <button onClick={() => handleStatusChange('draft')} disabled={statusSaving}
                  style={{ background: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0', borderRadius: 8, padding: '6px 14px', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                  📝 Set Draft
                </button>
              )}
              {property?.status !== 'inactive' && (
                <button onClick={() => handleStatusChange('inactive')} disabled={statusSaving}
                  style={{ background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: 8, padding: '6px 14px', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                  ⏸ Deactivate
                </button>
              )}
              <button onClick={() => setShowLaunchDate(!showLaunchDate)}
                style={{ background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe', borderRadius: 8, padding: '6px 14px', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                🗓 Launch Date
              </button>
              <button onClick={() => setShowRampDates(!showRampDates)}
                style={{ background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0', borderRadius: 8, padding: '6px 14px', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                📧💳 Ramp & Go-Live
              </button>
            </div>

            {/* Launch date picker */}
            {showLaunchDate && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', background: 'white', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 14px' }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#64748b' }}>Launch Date:</label>
                <input type="date" value={launchDate} onChange={e => setLaunchDate(e.target.value)}
                  style={{ border: '1px solid #e2e8f0', borderRadius: 6, padding: '4px 8px', fontSize: 13 }} />
                <button onClick={() => handleStatusChange(property?.status || 'draft')} disabled={statusSaving}
                  style={{ background: '#2563eb', color: 'white', border: 'none', borderRadius: 6, padding: '5px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                  Save
                </button>
              </div>
            )}

            {/* Ramp-up and Go-live date picker */}
            {showRampDates && (
              <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 10, padding: '14px 18px' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Property Timeline</div>
                <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 }}>Ramp-Up Date</label>
                    <input type="date" value={rampUpDate} onChange={e => setRampUpDate(e.target.value)}
                      style={{ border: '1px solid #e2e8f0', borderRadius: 6, padding: '6px 10px', fontSize: 13 }} />
                    <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>Emails enabled after this date</div>
                  </div>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 }}>Go-Live Date</label>
                    <input type="date" value={goLiveDate} onChange={e => setGoLiveDate(e.target.value)}
                      style={{ border: '1px solid #e2e8f0', borderRadius: 6, padding: '6px 10px', fontSize: 13 }} />
                    <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>Billing enabled after this date</div>
                  </div>
                  <button onClick={() => handleStatusChange(property?.status || 'draft')} disabled={statusSaving}
                    style={{ background: '#16a34a', color: 'white', border: 'none', borderRadius: 6, padding: '7px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                    {statusSaving ? 'Saving...' : 'Save Dates'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {error && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '12px 16px', color: '#dc2626', marginBottom: 16, fontSize: 14 }}>{error}</div>}
        {success && <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '12px 16px', color: '#16a34a', marginBottom: 16, fontSize: 14 }}>{success}</div>}

        {/* Status info banner for non-active properties */}
        {property?.status === 'draft' && (
          <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontSize: 14, color: '#64748b' }}>
            📝 This property is in <strong>Draft</strong> — it is not visible to the landlord or tenants. Click <strong>Activate</strong> to make it live.
          </div>
        )}
        {property?.status === 'inactive' && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontSize: 14, color: '#dc2626' }}>
            ⏸ This property is <strong>Inactive</strong> — tenants cannot log in and the landlord cannot see it. All data is preserved. Click <strong>Activate</strong> to restore access.
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
          {[
            { label: 'Total Units', value: units.length },
            { label: 'Active Tenants', value: activeUnits, color: '#16a34a' },
            { label: 'Adoption Rate', value: units.length ? Math.round(activeUnits / units.length * 100) + '%' : '0%', color: '#2563eb' },
            { label: 'Contract Expires', value: fmtDate(property?.contract_end_date), color: expiryWarning ? '#dc2626' : undefined },
          ].map((s, i) => (
            <div key={i} style={{ background: 'white', borderRadius: 12, padding: '1.2rem 1.5rem', border: '1px solid #e2e8f0' }}>
              <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#94a3b8', marginBottom: 6 }}>{s.label}</div>
              <div style={{ fontSize: '1.6rem', fontWeight: 700, color: s.color || '#1e293b' }}>{s.value}</div>
            </div>
          ))}
        </div>

        {expiryWarning && (
          <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '12px 16px', color: '#92400e', marginBottom: 16, fontSize: 14 }}>
            ⚠️ Contract expires in {daysToExpiry} days — renewal action needed!
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          <div style={{ background: 'white', borderRadius: 16, border: '1px solid #e2e8f0', padding: '1.8rem' }}>
            <h2 style={{ fontSize: '1rem', fontWeight: 700, color: '#1e293b', marginBottom: '1.2rem' }}>🔌 ISP & Contract</h2>
            {editing ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div>
                  <label style={lbl}>ISP Provider</label>
                  <select name="isp" value={form.isp || ''} onChange={handleChange} style={inp}>
                    <option value="">Select ISP</option>
                    {Object.keys(ISP_UPGRADE_URLS).map(isp => <option key={isp}>{isp}</option>)}
                  </select>
                </div>
                <div>
                  <label style={lbl}>Account Number</label>
                  <input name="isp_account_number" value={form.isp_account_number || ''} onChange={handleChange} style={inp} placeholder="ISP account number" />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div>
                    <label style={lbl}>Contract Start</label>
                    <input name="contract_start_date" type="date" value={form.contract_start_date?.split('T')[0] || ''} onChange={handleChange} style={inp} />
                  </div>
                  <div>
                    <label style={lbl}>Contract End</label>
                    <input name="contract_end_date" type="date" value={form.contract_end_date?.split('T')[0] || ''} onChange={handleChange} style={inp} />
                  </div>
                </div>
                {isSuperAdmin && (
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                      <label style={{ ...lbl, color: '#2563eb', marginBottom: 0 }}>📋 ISP Contacts</label>
                      <button onClick={() => setShowNewContact(!showNewContact)} style={{ background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe', borderRadius: 6, padding: '3px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                        {showNewContact ? '✕ Cancel' : '+ Add New'}
                      </button>
                    </div>
                    {ispContacts.length > 0 && (
                      <select onChange={e => handleContactSelect(e.target.value)} style={{ ...inp, borderColor: '#bfdbfe', background: '#eff6ff', marginBottom: 8 }}>
                        <option value="">— Pick a contact to autofill —</option>
                        {ispContacts.map(c => (
                          <option key={c.id} value={c.id}>{c.first_name} {c.last_name}{c.title ? ' — ' + c.title : ''}</option>
                        ))}
                      </select>
                    )}
                    {showNewContact && (
                      <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', marginBottom: 2 }}>New ISP Contact</div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                          <input placeholder="First name *" value={newContact.first_name} onChange={e => setNewContact(p => ({ ...p, first_name: e.target.value }))} style={{ ...inp, fontSize: 12 }} />
                          <input placeholder="Last name *" value={newContact.last_name} onChange={e => setNewContact(p => ({ ...p, last_name: e.target.value }))} style={{ ...inp, fontSize: 12 }} />
                        </div>
                        <input placeholder="Title (e.g. Senior Account Manager)" value={newContact.title} onChange={e => setNewContact(p => ({ ...p, title: e.target.value }))} style={{ ...inp, fontSize: 12 }} />
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                          <input placeholder="Email" value={newContact.email} onChange={e => setNewContact(p => ({ ...p, email: e.target.value }))} style={{ ...inp, fontSize: 12 }} />
                          <input placeholder="Phone" value={newContact.phone} onChange={e => setNewContact(p => ({ ...p, phone: e.target.value }))} style={{ ...inp, fontSize: 12 }} />
                        </div>
                        <button onClick={saveNewContact} disabled={savingContact || !newContact.first_name || !newContact.last_name} style={{ background: '#2563eb', color: 'white', border: 'none', borderRadius: 7, padding: '7px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', opacity: (!newContact.first_name || !newContact.last_name) ? 0.5 : 1 }}>
                          {savingContact ? 'Saving...' : 'Save Contact'}
                        </button>
                      </div>
                    )}
                  </div>
                )}
                {isSuperAdmin && <div>
                  <label style={lbl}>ISP Contact Name</label>
                  <input name="isp_contact_name" value={form.isp_contact_name || ''} onChange={handleChange} style={inp} placeholder="Rep name" />
                </div>
                }
                {isSuperAdmin && <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div>
                    <label style={lbl}>ISP Contact Email</label>
                    <input name="isp_contact_email" value={form.isp_contact_email || ''} onChange={handleChange} style={inp} placeholder="rep@isp.com" />
                  </div>
                  <div>
                    <label style={lbl}>ISP Contact Phone</label>
                    <input name="isp_contact_phone" value={form.isp_contact_phone || ''} onChange={handleChange} style={inp} placeholder="555-000-0000" />
                  </div>
                </div>}
                <div>
                  <label style={lbl}>Renewal Terms</label>
                  <input name="contract_renewal_terms" value={form.contract_renewal_terms || ''} onChange={handleChange} style={inp} placeholder="e.g. Auto-renews annually" />
                </div>
                <div>
                  <label style={lbl}>Contract Notes</label>
                  <textarea name="contract_notes" value={form.contract_notes || ''} onChange={handleChange} style={{ ...inp, minHeight: 80, resize: 'vertical' }} placeholder="Special terms, exclusivity clauses, penalties..." />
                </div>
                <div>
                  <label style={lbl}>ISP Upgrade URL</label>
                  <input name="isp_upgrade_url" value={form.isp_upgrade_url || ''} onChange={handleChange} style={inp} placeholder="Auto-filled when ISP selected" />
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {[
                  { label: 'ISP', value: property?.isp },
                  { label: 'Account Number', value: property?.isp_account_number },
                  { label: 'Contract Start', value: fmtDate(property?.contract_start_date) },
                  { label: 'Contract End', value: fmtDate(property?.contract_end_date) },
                  ...(isSuperAdmin ? [
                    { label: 'ISP Contact', value: property?.isp_contact_name },
                    { label: 'ISP Email', value: property?.isp_contact_email },
                    { label: 'ISP Phone', value: property?.isp_contact_phone },
                  ] : []),
                  { label: 'Renewal Terms', value: property?.contract_renewal_terms },
                  { label: 'Upgrade URL', value: property?.isp_upgrade_url, link: true },
                ].map((row, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, paddingBottom: 8, borderBottom: '1px solid #f1f5f9' }}>
                    <span style={{ color: '#94a3b8', fontWeight: 500 }}>{row.label}</span>
                    {row.link && row.value
                      ? <a href={row.value} target="_blank" rel="noreferrer" style={{ color: '#2563eb', fontSize: 12, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.value}</a>
                      : <span style={{ color: '#1e293b', fontWeight: 500 }}>{row.value || '—'}</span>
                    }
                  </div>
                ))}
                {property?.contract_notes && (
                  <div style={{ background: '#f8fafc', borderRadius: 8, padding: '10px 12px', fontSize: 13, color: '#475569', marginTop: 4 }}>
                    <div style={{ fontWeight: 600, marginBottom: 4, color: '#94a3b8', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Notes</div>
                    {property.contract_notes}
                  </div>
                )}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div style={{ background: 'white', borderRadius: 16, border: '1px solid #e2e8f0', padding: '1.8rem' }}>
              <h2 style={{ fontSize: '1rem', fontWeight: 700, color: '#1e293b', marginBottom: '1.2rem' }}>📡 Service Details</h2>
              {editing ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div>
                    <label style={lbl}>Minimum Internet Speed (contract floor)</label>
                    <input name="min_internet_speed" value={form.min_internet_speed || ''} onChange={handleChange} style={inp} placeholder="e.g. 1 Gbps" />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <div>
                      <label style={lbl}>Our Base Cost/mo</label>
                      <input name="base_monthly_cost" type="number" value={form.base_monthly_cost || ''} onChange={handleChange} style={inp} placeholder="35.00" />
                    </div>
                    <div>
                      <label style={lbl}>Our Margin/unit</label>
                      <input name="our_margin" type="number" value={form.our_margin || ''} onChange={handleChange} style={inp} placeholder="14.99" />
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <input type="checkbox" id="cable_included" name="cable_included" checked={form.cable_included || false} onChange={handleChange} style={{ width: 16, height: 16 }} />
                    <label htmlFor="cable_included" style={{ fontSize: 14, color: '#475569', fontWeight: 500 }}>Cable included in contract</label>
                  </div>
                  {form.cable_included && (
                    <div>
                      <label style={lbl}>Cable Package Details</label>
                      <input name="cable_package_details" value={form.cable_package_details || ''} onChange={handleChange} style={inp} placeholder="e.g. Basic cable, 50 channels" />
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <input type="checkbox" id="phone_included" name="phone_included" checked={form.phone_included || false} onChange={handleChange} style={{ width: 16, height: 16 }} />
                    <label htmlFor="phone_included" style={{ fontSize: 14, color: '#475569', fontWeight: 500 }}>Phone included in contract</label>
                  </div>
                  {form.phone_included && (
                    <div>
                      <label style={lbl}>Phone Package Details</label>
                      <input name="phone_package_details" value={form.phone_package_details || ''} onChange={handleChange} style={inp} placeholder="e.g. Unlimited local & long distance" />
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {[
                    { label: 'Min Speed (floor)', value: property?.min_internet_speed },
                    { label: 'Base Cost/mo', value: fmtMoney(property?.base_monthly_cost) },
                    { label: 'Our Margin/unit', value: fmtMoney(property?.our_margin) },
                  ].map((row, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, paddingBottom: 8, borderBottom: '1px solid #f1f5f9' }}>
                      <span style={{ color: '#94a3b8', fontWeight: 500 }}>{row.label}</span>
                      <span style={{ color: '#1e293b', fontWeight: 600 }}>{row.value || '—'}</span>
                    </div>
                  ))}
                  <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                    <span style={{ background: property?.cable_included ? '#dcfce7' : '#f1f5f9', color: property?.cable_included ? '#16a34a' : '#94a3b8', borderRadius: 100, padding: '4px 12px', fontSize: 12, fontWeight: 600 }}>
                      📺 Cable {property?.cable_included ? 'Included' : 'Not Included'}
                    </span>
                    <span style={{ background: property?.phone_included ? '#dcfce7' : '#f1f5f9', color: property?.phone_included ? '#16a34a' : '#94a3b8', borderRadius: 100, padding: '4px 12px', fontSize: 12, fontWeight: 600 }}>
                      📞 Phone {property?.phone_included ? 'Included' : 'Not Included'}
                    </span>
                  </div>
                  {property?.cable_included && property?.cable_package_details && <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>Cable: {property.cable_package_details}</div>}
                  {property?.phone_included && property?.phone_package_details && <div style={{ fontSize: 12, color: '#64748b' }}>Phone: {property.phone_package_details}</div>}
                </div>
              )}
            </div>

            <div style={{ background: '#eff6ff', borderRadius: 16, border: '1px solid #bfdbfe', padding: '1.5rem' }}>
              <h2 style={{ fontSize: '1rem', fontWeight: 700, color: '#1e293b', marginBottom: '0.8rem' }}>🔗 Tenant Signup Link</h2>
              <div style={{ fontSize: 13, color: '#3b82f6', background: 'white', borderRadius: 8, padding: '10px 14px', fontFamily: 'monospace', wordBreak: 'break-all', border: '1px solid #bfdbfe' }}>
                {'https://bulknet-frontend.vercel.app/signup/' + property?.slug}
              </div>
              <button onClick={() => navigator.clipboard.writeText('https://bulknet-frontend.vercel.app/signup/' + property?.slug)}
                style={{ marginTop: 10, background: '#2563eb', color: 'white', border: 'none', borderRadius: 8, padding: '7px 16px', fontSize: 13, cursor: 'pointer', fontWeight: 600 }}>
                Copy Link
              </button>
            </div>
          </div>
        </div>

        <div style={{ background: 'white', borderRadius: 16, border: '1px solid #e2e8f0', padding: '1.8rem', marginTop: 20 }}>
          <h2 style={{ fontSize: '1rem', fontWeight: 700, color: '#1e293b', marginBottom: '1.2rem' }}>🏠 Unit Roster ({units.length} units)</h2>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  {['Unit', 'Status', 'Tenant', 'Plan', 'Monthly Rate'].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#64748b', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {units.map((u, i) => (
                  <tr key={u.id} style={{ borderTop: '1px solid #f1f5f9', background: i % 2 === 0 ? 'white' : '#fafafa' }}>
                    <td style={{ padding: '12px 14px', fontWeight: 700 }}>{u.unit_number}</td>
                    <td style={{ padding: '12px 14px' }}>
                      <span style={{ background: u.tenant_id ? '#dcfce7' : '#f1f5f9', color: u.tenant_id ? '#16a34a' : '#94a3b8', borderRadius: 100, padding: '3px 10px', fontSize: 11, fontWeight: 600 }}>
                        {u.tenant_id ? 'Occupied' : 'Vacant'}
                      </span>
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      {u.tenant_name
                        ? <div><div style={{ fontWeight: 600, color: '#1e293b' }}>{u.tenant_name}</div><div style={{ fontSize: 11, color: '#94a3b8' }}>{u.tenant_email}</div></div>
                        : <span style={{ color: '#94a3b8' }}>—</span>}
                    </td>
                    <td style={{ padding: '12px 14px', color: '#475569' }}>{u.plan_name || '—'}</td>
                    <td style={{ padding: '12px 14px', fontWeight: 600, color: u.bulknet_price ? '#16a34a' : '#94a3b8' }}>
                      {u.bulknet_price ? '$' + parseFloat(u.bulknet_price).toFixed(2) + '/mo' : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pre-Approvals */}
        <div style={{ background: 'white', borderRadius: 16, border: '1px solid #e2e8f0', padding: '1.8rem', marginTop: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.2rem' }}>
            <h2 style={{ fontSize: '1rem', fontWeight: 700, color: '#1e293b', margin: 0 }}>📋 Pre-Approvals / Expected Move-Ins ({preApprovals.length})</h2>
            <button onClick={() => setShowAddPA(!showAddPA)}
              style={{ background: '#2563eb', color: 'white', border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              {showAddPA ? 'Cancel' : '+ Add Pre-Approval'}
            </button>
          </div>

          {showAddPA && (
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: 16, marginBottom: 16 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 10 }}>
                <div><label style={lbl}>Unit *</label><input value={paForm.unit_number} onChange={e => setPaForm({ ...paForm, unit_number: e.target.value })} style={inp} placeholder="e.g. 4B" /></div>
                <div><label style={lbl}>First Name</label><input value={paForm.first_name} onChange={e => setPaForm({ ...paForm, first_name: e.target.value })} style={inp} /></div>
                <div><label style={lbl}>Last Name</label><input value={paForm.last_name} onChange={e => setPaForm({ ...paForm, last_name: e.target.value })} style={inp} /></div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 10 }}>
                <div><label style={lbl}>Email</label><input type="email" value={paForm.email} onChange={e => setPaForm({ ...paForm, email: e.target.value })} style={inp} /></div>
                <div><label style={lbl}>Phone</label><input value={paForm.phone} onChange={e => setPaForm({ ...paForm, phone: e.target.value })} style={inp} /></div>
                <div><label style={lbl}>Move-In Date</label><input type="date" value={paForm.move_in_date} onChange={e => setPaForm({ ...paForm, move_in_date: e.target.value })} style={inp} /></div>
              </div>
              <div style={{ marginBottom: 10 }}><label style={lbl}>Notes</label><input value={paForm.notes} onChange={e => setPaForm({ ...paForm, notes: e.target.value })} style={inp} placeholder="Optional notes" /></div>
              <button disabled={!paForm.unit_number || savingPA} onClick={async () => {
                setSavingPA(true);
                try {
                  await api.post('/pre-approvals', { ...paForm, property_id: id });
                  const res = await api.get('/pre-approvals?property_id=' + id);
                  setPreApprovals(res.data.pre_approvals || []);
                  setPaForm({ unit_number: '', first_name: '', last_name: '', email: '', phone: '', move_in_date: '', notes: '' });
                  setShowAddPA(false);
                } catch (err) { alert(err.response?.data?.error || 'Could not save'); }
                finally { setSavingPA(false); }
              }} style={{ background: '#2563eb', color: 'white', border: 'none', borderRadius: 8, padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                {savingPA ? 'Saving...' : 'Save Pre-Approval'}
              </button>
            </div>
          )}

          {preApprovals.length === 0 ? (
            <div style={{ color: '#94a3b8', fontSize: 13, textAlign: 'center', padding: 20 }}>No pre-approvals yet.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    {['Unit', 'Name', 'Email', 'Phone', 'Move-In', 'Status', ''].map(h => (
                      <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#64748b', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preApprovals.map((pa, i) => (
                    <tr key={pa.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '10px 14px', fontWeight: 700 }}>{pa.unit_number}</td>
                      <td style={{ padding: '10px 14px' }}>{pa.first_name} {pa.last_name}</td>
                      <td style={{ padding: '10px 14px', fontSize: 12 }}>{pa.email || '—'}</td>
                      <td style={{ padding: '10px 14px', fontSize: 12 }}>{pa.phone || '—'}</td>
                      <td style={{ padding: '10px 14px', fontSize: 12 }}>{pa.move_in_date ? fmtDate(pa.move_in_date) : '—'}</td>
                      <td style={{ padding: '10px 14px' }}>
                        <span style={{
                          background: pa.status === 'activated' ? '#dcfce7' : pa.status === 'expired' ? '#fee2e2' : '#fef9c3',
                          color: pa.status === 'activated' ? '#16a34a' : pa.status === 'expired' ? '#dc2626' : '#854d0e',
                          borderRadius: 100, padding: '3px 10px', fontSize: 11, fontWeight: 600,
                        }}>{pa.status}</span>
                      </td>
                      <td style={{ padding: '10px 14px' }}>
                        {pa.status === 'pending' && (
                          <button onClick={async () => {
                            if (!window.confirm('Delete this pre-approval?')) return;
                            await api.delete('/pre-approvals/' + pa.id);
                            setPreApprovals(prev => prev.filter(p => p.id !== pa.id));
                          }} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 12 }}>Delete</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

      <PropertyMap address={property?.address} city={property?.city} state={property?.state} zip={property?.zip} name={property?.name} />

        <LegalFooter />
      </div>
    </div>
  );
}
